import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { AccessError, GetYtdReport } from "../services/ApiService";
import { downloadCsv } from "../services/CsvService";
import { Alert, Button, Card, Spin, Table, Typography } from "antd";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";

const { Title, Text } = Typography;

// Categorical palette — fixed hue order, CVD-safe for up to 8 adjacent series (dataviz skill).
const SECTOR_COLORS = [
  "#2a78d6", // blue
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
];
// Spend of advertisers with no sector in rev_group_metadata; shown last, in grey, as on
// the YTD Dashboard's Sector table.
const UNKNOWN_SECTOR_LABEL = "Unknown (no sector)";
const UNKNOWN_SECTOR_COLOR = "#bfbfbf";

function fmtUsd(n) {
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtMonthLabel(month, isCurrentMonth) {
  const [y, m] = month.split("-").map(Number);
  const label = new Date(y, m - 1, 1).toLocaleString("en-US", { month: "short", year: "numeric" });
  return isCurrentMonth ? `${label} (MTD)` : label;
}

// Pivots [{month, sector, partner_cost_usd}] rows into one row per month with a column
// per sector, in a fixed alphabetical order so each sector keeps the same stack position
// and color across every bar. Unknown comes last (top of the stack, last column).
function buildChartData(byMonthSector, currentMonth) {
  const months = [...new Set(byMonthSector.map((r) => r.month))].sort();
  const sectors = [...new Set(byMonthSector.map((r) => r.sector))];
  const sectorOrder = [
    ...sectors.filter((s) => s !== UNKNOWN_SECTOR_LABEL).sort(),
    ...sectors.filter((s) => s === UNKNOWN_SECTOR_LABEL),
  ];

  const colorBySector = {};
  sectorOrder
    .filter((s) => s !== UNKNOWN_SECTOR_LABEL)
    .forEach((sector, i) => {
      colorBySector[sector] = SECTOR_COLORS[i % SECTOR_COLORS.length];
    });
  colorBySector[UNKNOWN_SECTOR_LABEL] = UNKNOWN_SECTOR_COLOR;

  const rows = months.map((month) => {
    const row = { month, label: fmtMonthLabel(month, month === currentMonth) };
    for (const sector of sectorOrder) row[sector] = 0;
    for (const r of byMonthSector) {
      if (r.month === month) row[r.sector] += r.partner_cost_usd; // blank and "Unknown" share a label
    }
    return row;
  });

  return { rows, sectorOrder, colorBySector };
}

function MomStackedBarChart({ rows, sectorOrder, colorBySector }) {
  const topSector = sectorOrder[sectorOrder.length - 1];

  return (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart data={rows} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={fmtUsd} tick={{ fontSize: 12 }} />
        <ReTooltip formatter={(v, name) => [fmtUsd(v), name]} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {sectorOrder.map((sector) => (
          <Bar
            key={sector}
            dataKey={sector}
            name={sector}
            stackId="spend"
            fill={colorBySector[sector]}
            stroke="#fff"
            strokeWidth={2}
            maxBarSize={64}
            radius={sector === topSector ? [4, 4, 0, 0] : 0}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function MomTable({ rows, sectorOrder }) {
  const columns = [
    { title: "Month", dataIndex: "label", key: "label", fixed: "left", width: 120 },
    ...sectorOrder.map((sector) => ({
      title: sector,
      dataIndex: sector,
      key: sector,
      align: "right",
      render: (v) => fmtUsd(v || 0),
    })),
    {
      title: "Total",
      key: "total",
      align: "right",
      render: (_, r) => <b>{fmtUsd(sectorOrder.reduce((sum, s) => sum + (r[s] || 0), 0))}</b>,
    },
  ];

  return (
    <Table
      dataSource={rows}
      columns={columns}
      rowKey="month"
      size="small"
      pagination={false}
      scroll={{ x: 200 + sectorOrder.length * 130 }}
      style={{ marginTop: 16 }}
    />
  );
}

export default function MomReport() {
  const auth = useAuth();
  const token = auth.user?.id_token;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    GetYtdReport(token)
      .then(setData)
      .catch((e) => setError(e instanceof AccessError ? e.message : "Failed to load MoM report."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 80, gap: 16 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
        <Text type="secondary">Loading MoM report…</Text>
      </div>
    </div>
  );
  if (error) return <Alert type="error" message={error} style={{ margin: 24 }} />;
  if (!data) return null;

  // "Unknown" (or blank) sector spend is kept, under one label, as on the YTD Dashboard.
  const byMonthSector = (data.by_month_sector || []).map((r) =>
    !r.sector || r.sector.toLowerCase() === "unknown" ? { ...r, sector: UNKNOWN_SECTOR_LABEL } : r
  );
  if (!byMonthSector.length) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="No monthly spend data available yet." />
      </div>
    );
  }

  const currentMonth = data.mom_period ? data.mom_period.current_start.slice(0, 7) : null;
  const { rows, sectorOrder, colorBySector } = buildChartData(byMonthSector, currentMonth);

  const csvRows = rows.map((r) => {
    const row = { Month: r.label };
    let total = 0;
    for (const sector of sectorOrder) {
      row[sector] = r[sector] || 0;
      total += r[sector] || 0;
    }
    row.Total = total;
    return row;
  });

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <Title level={4} style={{ marginBottom: 20 }}>Month-over-Month Spend Report</Title>
      <Card
        title="Advertiser Spend by Month & Sector"
        bordered={false}
        style={{ marginBottom: 24 }}
        extra={(
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadCsv(csvRows, "mom_report.csv")}
          >
            Export CSV
          </Button>
        )}
      >
        <MomStackedBarChart rows={rows} sectorOrder={sectorOrder} colorBySector={colorBySector} />
        <MomTable rows={rows} sectorOrder={sectorOrder} />
      </Card>
    </div>
  );
}
